import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Collapse,
  IconButton,
  alpha,
} from '@mui/material';
import {
  RefreshOutlined,
  PlayArrowOutlined,
  CheckCircleOutlined,
  ErrorOutlineOutlined,
  RemoveCircleOutlineOutlined,
  HealthAndSafetyOutlined,
  ExpandMoreOutlined,
  ExpandLessOutlined,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../utils/datetime';
import apiService, { EndpointProbeResult, EndpointStatusSnapshot } from '../services/api';

interface AppGroup {
  app_id: string;
  app_name: string;
  results: EndpointProbeResult[];
  healthy: number;
  failing: number;
  skipped: number;
  worstLatency: number;
}

const StatusPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [snapshot, setSnapshot] = useState<EndpointStatusSnapshot | null>(null);
  const [loading, setLoading] = useState<'load' | 'run' | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' | 'info' }>({
    open: false, msg: '', severity: 'success',
  });

  const isSuperAdmin = user?.role === 'super_admin';

  const showSnack = (msg: string, severity: 'success' | 'error' | 'info' = 'success') =>
    setSnack({ open: true, msg, severity });

  const load = useCallback(async () => {
    setLoading('load');
    try {
      const s = await apiService.getEndpointStatus();
      setSnapshot(s);
    } catch (e: any) {
      showSnack(e.response?.data?.detail || 'Failed to fetch status', 'error');
    } finally {
      setLoading(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setLoading('run');
    try {
      const s = await apiService.runEndpointStatusProbe();
      setSnapshot(s);
      showSnack('Probe complete');
    } catch (e: any) {
      showSnack(e.response?.data?.detail || 'Probe failed', 'error');
    } finally {
      setLoading(null);
    }
  };

  const rows: EndpointProbeResult[] = useMemo(
    () => snapshot?.results ?? [],
    [snapshot?.results],
  );

  const summary = useMemo(() => {
    let up = 0, down = 0, skipped = 0;
    const apps = new Set<string>();
    for (const r of rows) {
      apps.add(r.app_id);
      if (r.skipped || r.ok === null) skipped += 1;
      else if (r.ok) up += 1;
      else down += 1;
    }
    return { up, down, skipped, total: rows.length, apps: apps.size };
  }, [rows]);

  const groups: AppGroup[] = useMemo(() => {
    const map = new Map<string, AppGroup>();
    for (const r of rows) {
      let g = map.get(r.app_id);
      if (!g) {
        g = { app_id: r.app_id, app_name: r.app_name, results: [], healthy: 0, failing: 0, skipped: 0, worstLatency: 0 };
        map.set(r.app_id, g);
      }
      g.results.push(r);
      if (r.skipped || r.ok === null) g.skipped += 1;
      else if (r.ok) g.healthy += 1;
      else g.failing += 1;
      if (!r.skipped && r.latency_ms > g.worstLatency) g.worstLatency = r.latency_ms;
    }
    // Sort: failing first, then by app name.
    return Array.from(map.values()).sort((a, b) => {
      if (a.failing !== b.failing) return b.failing - a.failing;
      return a.app_name.localeCompare(b.app_name);
    });
  }, [rows]);

  const toggle = (appId: string) => setExpanded(prev => ({ ...prev, [appId]: !prev[appId] }));

  const sectionBorder = isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)';

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', color: 'text.primary', letterSpacing: '-0.02em' }}>
            Status
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mt: 0.25 }}>
            {user?.role === 'app_viewer'
              ? 'Wrapper health for the apps you have access to'
              : "Every app's wrapper key probed independently against the endpoints it has enabled"}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={load}
            disabled={loading !== null}
            startIcon={loading === 'load' ? <CircularProgress size={16} /> : <RefreshOutlined />}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
          <Tooltip title={isSuperAdmin ? '' : 'Only super admins can trigger a probe (consumes credit on each app\'s key).'}>
            <span>
              <Button
                variant="contained"
                onClick={run}
                disabled={loading !== null || !isSuperAdmin}
                startIcon={loading === 'run' ? <CircularProgress size={16} color="inherit" /> : <PlayArrowOutlined />}
                sx={{ textTransform: 'none' }}
              >
                Run probe now
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2.5, mb: 2.5, flexWrap: 'wrap' }}>
        <SummaryTile icon={<HealthAndSafetyOutlined />} label="Apps probed" value={summary.apps} color="#3B82F6" isDark={isDark} />
        <SummaryTile icon={<HealthAndSafetyOutlined />} label="Total probes" value={summary.total} color="#6366F1" isDark={isDark} />
        <SummaryTile icon={<CheckCircleOutlined />} label="Healthy" value={summary.up} color="#10B981" isDark={isDark} />
        <SummaryTile icon={<ErrorOutlineOutlined />} label="Failing" value={summary.down} color="#EF4444" isDark={isDark} />
        <SummaryTile icon={<RemoveCircleOutlineOutlined />} label="Skipped" value={summary.skipped} color="#94A3B8" isDark={isDark} />
      </Box>

      <Paper elevation={1} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: sectionBorder }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary' }}>
            Latest snapshot
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.25 }}>
            {snapshot?.timestamp
              ? `Captured ${formatDateTime(snapshot.timestamp, user?.timezone)}`
              : 'No snapshot yet — background sweep runs every 30 minutes.'}
            {snapshot?.triggered_by ? ` · triggered by ${snapshot.triggered_by}` : ''}
          </Typography>
        </Box>

        {snapshot?.note && (
          <Alert severity="info" sx={{ m: 3, fontSize: '0.85rem' }}>{snapshot.note}</Alert>
        )}

        {groups.length === 0 && !snapshot?.note && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
              {user?.role === 'app_viewer'
                ? 'No probe results for the apps you have access to yet. The platform runs a sweep every 30 minutes — check back shortly.'
                : 'No results yet. Trigger a probe or wait for the next scheduled sweep.'}
            </Typography>
          </Box>
        )}

        {groups.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 44 }} />
                <TableCell sx={hCell}>App</TableCell>
                <TableCell sx={hCell} align="center">Healthy</TableCell>
                <TableCell sx={hCell} align="center">Failing</TableCell>
                <TableCell sx={hCell} align="center">Skipped</TableCell>
                <TableCell sx={hCell} align="right">Slowest probe</TableCell>
                <TableCell sx={hCell}>Overall</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g) => {
                const overall: 'healthy' | 'partial' | 'failing' | 'unknown' =
                  g.failing > 0 ? 'failing'
                  : g.healthy > 0 ? 'healthy'
                  : 'unknown';
                const open = !!expanded[g.app_id];
                return (
                  <React.Fragment key={g.app_id}>
                    <TableRow hover sx={{ '& > td': { borderBottom: open ? 'none' : undefined } }}>
                      <TableCell>
                        <IconButton size="small" onClick={() => toggle(g.app_id)}>
                          {open ? <ExpandLessOutlined fontSize="small" /> : <ExpandMoreOutlined fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{g.app_name}</TableCell>
                      <TableCell align="center" sx={cellMono}>{g.healthy}</TableCell>
                      <TableCell align="center" sx={{ ...cellMono, color: g.failing > 0 ? '#EF4444' : undefined, fontWeight: g.failing > 0 ? 700 : undefined }}>
                        {g.failing}
                      </TableCell>
                      <TableCell align="center" sx={cellMono}>{g.skipped}</TableCell>
                      <TableCell align="right" sx={cellMono}>
                        {g.worstLatency ? `${g.worstLatency}ms` : '—'}
                      </TableCell>
                      <TableCell>
                        <OverallChip status={overall} isDark={isDark} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ p: 0, borderBottom: sectionBorder }}>
                        <Collapse in={open} unmountOnExit>
                          <Box sx={{ px: 3, py: 2, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#F8FAFC' }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={hCellSm}>Endpoint</TableCell>
                                  <TableCell sx={hCellSm}>Model</TableCell>
                                  <TableCell sx={hCellSm}>Status</TableCell>
                                  <TableCell sx={hCellSm} align="right">HTTP</TableCell>
                                  <TableCell sx={hCellSm} align="right">Latency</TableCell>
                                  <TableCell sx={hCellSm}>Detail</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {g.results.map((r) => (
                                  <TableRow key={`${r.app_id}-${r.endpoint}`}>
                                    <TableCell sx={cellMono}>
                                      {r.endpoint}
                                      {r.description && (
                                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontFamily: 'inherit' }}>
                                          {r.description}
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell sx={cellMono}>{r.model || '—'}</TableCell>
                                    <TableCell><StatusChip result={r} isDark={isDark} /></TableCell>
                                    <TableCell align="right" sx={cellMono}>{r.status_code || '—'}</TableCell>
                                    <TableCell align="right" sx={cellMono}>{r.skipped ? '—' : `${r.latency_ms}ms`}</TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem', color: r.ok ? 'text.secondary' : '#EF4444', maxWidth: 360, wordBreak: 'break-word' }}>
                                      {r.skipped ? (r.error || 'skipped') : (r.error ?? (r.ok ? 'OK' : ''))}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled">{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

const hCell = { fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase' as const, letterSpacing: '0.05em' };
const hCellSm = { fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase' as const, letterSpacing: '0.05em', color: 'text.secondary' };
const cellMono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.78rem' };

const SummaryTile: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number;
  color: string;
  isDark: boolean;
}> = ({ icon, label, value, color, isDark }) => (
  <Paper elevation={1} sx={{ flex: '1 1 160px', minWidth: 160, borderRadius: '14px', px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
    <Box sx={{
      width: 36, height: 36,
      bgcolor: isDark ? alpha(color, 0.12) : alpha(color, 0.1),
      border: `1px solid ${alpha(color, isDark ? 0.25 : 0.2)}`,
      color,
      borderRadius: '10px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Typography>
      <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: 'text.primary', letterSpacing: '-0.02em' }}>{value}</Typography>
    </Box>
  </Paper>
);

const OverallChip: React.FC<{ status: 'healthy' | 'partial' | 'failing' | 'unknown'; isDark: boolean }> = ({ status, isDark }) => {
  if (status === 'failing') {
    return <Chip size="small" label="Failing" sx={{ bgcolor: isDark ? alpha('#EF4444', 0.15) : '#FEE2E2', color: '#DC2626', fontWeight: 700, fontSize: '0.7rem' }} />;
  }
  if (status === 'healthy') {
    return <Chip size="small" label="Healthy" sx={{ bgcolor: isDark ? alpha('#10B981', 0.15) : '#DCFCE7', color: '#059669', fontWeight: 700, fontSize: '0.7rem' }} />;
  }
  return <Chip size="small" label="—" sx={{ bgcolor: isDark ? alpha('#94A3B8', 0.15) : '#F1F5F9', color: '#64748B', fontWeight: 700, fontSize: '0.7rem' }} />;
};

const StatusChip: React.FC<{ result: EndpointProbeResult; isDark: boolean }> = ({ result, isDark }) => {
  if (result.skipped || result.ok === null) {
    return <Chip size="small" label="Skipped" sx={{ bgcolor: isDark ? alpha('#94A3B8', 0.15) : '#F1F5F9', color: '#64748B', fontWeight: 700, fontSize: '0.7rem' }} />;
  }
  if (result.ok) {
    return <Chip size="small" label="Healthy" sx={{ bgcolor: isDark ? alpha('#10B981', 0.15) : '#DCFCE7', color: '#059669', fontWeight: 700, fontSize: '0.7rem' }} />;
  }
  return <Chip size="small" label="Failing" sx={{ bgcolor: isDark ? alpha('#EF4444', 0.15) : '#FEE2E2', color: '#DC2626', fontWeight: 700, fontSize: '0.7rem' }} />;
};

export default StatusPage;
